import {
    IDatasetTypeMetadata,
    IDynamicDatasetTypeMetadata
} from 'app/extensibility/metadatas/resource-type/dataset-type-metadata';
import { adfModelTypeMap } from '../consts/adf-model-type-map-const';
import {
    partitionOptionConst,
    partitionOptions,
    partitionOptionForwardLinkMap,
    sapColumnDelimiterMetadata
} from '../consts/metadata-consts';
import { format } from 'shell-models-lib';
import { getServiceInstance } from 'app/multi-contexts/modules/root/root.application';
import { ExposureControlService } from 'app/services/exposure-control/base-exposure-control.service';
import { GlobalVariables } from 'app/services/globals';

export const sapTablePartitionOptions = [
    partitionOptionConst.none,
    partitionOptionConst.partitionOnInt,
    partitionOptionConst.partitionOnCalendarYear,
    partitionOptionConst.partitionOnCalendarMonth,
    partitionOptionConst.partitionOnCalendarDate,
    partitionOptionConst.partitionOnTime
];

export const sapTableResourceMetadata: IDynamicDatasetTypeMetadata = async (injector) => {
    const exposureControlService = await getServiceInstance<ExposureControlService>(injector, 'ExposureControlService');
    const metadata: IDatasetTypeMetadata = {
        type: adfModelTypeMap.sapTable.datasetType,
        displayName: stringResources.SapTable_DisplayText,
        supportLinkedServiceTypes: [adfModelTypeMap.sapTable.linkedServiceType],
        isSource: true,
        isSink: false,
        datasetKind: 'table',
        icon: {
            className: 'svg-i-100x100-SapTable'
        },
        isFirstClass: true,
        notSupportCopySourceQuery: true,
        isTableNameRequired: true,
        category: ['Database'],
        supportCreatedAsV2: true,
        notSupportPhysicalType: true,
        v1SupportSchema: true,
        supportParallelRead: true,
        copySourceControls: [
            {
                type: 'parameterizable',
                child: {
                    type: 'textbox-number',
                    bind: 'rowCount',
                    label: stringResources.RowCountLabel,
                    description: stringResources.SapTable_RowCount_Description
                },
                dynamicContentTextClass: 'input-spaced'
            },
            {
                type: 'parameterizable',
                hide: () => {
                    return !exposureControlService.showSapTableRowSkip;
                },
                child: {
                    type: 'textbox-number',
                    bind: 'rowSkips',
                    label: stringResources.ModelName_RowSkips,
                    description: stringResources.SapTable_RowSkips_Description
                },
                dynamicContentTextClass: 'input-spaced'
            },
            {
                type: 'parameterizable',
                child: {
                    type: 'columns-selection',
                    bind: 'rfcTableFields',
                    label: stringResources.ModelName_RfcTableFields,
                    description: stringResources.SapTable_RfcTableFields_Description,
                    placeholder: 'column0,column1',
                    includeCopySourceProperties: true,
                    reservedSourceProperties: ['customRfcReadTableFunctionModule']
                },
                dynamicContentTextClass: 'input-spaced'
            },
            {
                type: 'parameterizable',
                child: {
                    type: 'textarea',
                    bind: 'rfcTableOptions',
                    label: stringResources.ModelName_RfcTableOptions,
                    placeholder: `COLUMN0 EQ 'SOME VALUE'`,
                    description: `${stringResources.SapTable_RfcTableOptions_Description} ${stringResources.Click} ${stringResources.To_See_More_Details}`,
                    descriptionLink:
                        'https://docs.microsoft.com/azure/data-factory/connector-sap-table#copy-activity-properties'
                },
                dynamicContentTextClass: 'input-spaced'
            },
            {
                type: 'parameterizable',
                child: {
                    type: 'textbox',
                    bind: 'customRfcReadTableFunctionModule',
                    label: stringResources.ModelName_CustomRfcReadTableFunctionModule,
                    description: format(
                        stringResources.CustomRfcReadTableFunctionModule_DescriptionTemplate,
                        stringResources.SapTable_DisplayText
                    )
                },
                dynamicContentTextClass: 'input-spaced'
            },
            {
                type: 'radio-switch',
                label: stringResources.ModelName_Partition_Option,
                defaultValue: partitionOptionConst.none,
                options: partitionOptions.filter((p) => sapTablePartitionOptions.indexOf(p.value) >= 0),
                bind: 'partitionOption',
                descriptionLink: partitionOptionForwardLinkMap[adfModelTypeMap.sapTable.copySourceType],
                isParameterizable: true,
                cases: [
                    {
                        caseValue: partitionOptionConst.partitionOnInt,
                        children: [
                            {
                                type: 'parameterizable',
                                child: {
                                    type: 'columns-selection',
                                    bind: 'partitionColumnName',
                                    label: stringResources.ModelName_Partition_ColumnName,
                                    includeCopySourceProperties: true,
                                    multiple: false,
                                    excludeSourceProperties: ['partitionOption', 'partitionSettings']
                                },
                                dynamicContentTextClass: 'input-spaced'
                            },
                            {
                                type: 'parameterizable',
                                child: {
                                    type: 'textbox',
                                    bind: 'partitionUpperBound',
                                    placeholder: 'Number, e.g. 100',
                                    label: stringResources.ModelName_Partition_UpperBound
                                },
                                dynamicContentTextClass: 'input-spaced'
                            },
                            {
                                type: 'parameterizable',
                                child: {
                                    type: 'textbox',
                                    bind: 'partitionLowerBound',
                                    placeholder: 'Number, e.g. 1',
                                    label: stringResources.ModelName_Partition_LowerBound
                                },
                                dynamicContentTextClass: 'input-spaced'
                            },
                            {
                                type: 'parameterizable',
                                child: {
                                    type: 'textbox-number',
                                    bind: 'maxPartitionsNumber',
                                    label: stringResources.ModelName_MaxPartitionsNumber
                                },
                                dynamicContentTextClass: 'input-spaced'
                            }
                        ]
                    },
                    {
                        caseValue: partitionOptionConst.partitionOnCalendarYear,
                        children: [
                            {
                                type: 'parameterizable',
                                child: {
                                    type: 'columns-selection',
                                    bind: 'partitionColumnName',
                                    label: stringResources.ModelName_Partition_ColumnName,
                                    includeCopySourceProperties: true,
                                    multiple: false,
                                    excludeSourceProperties: ['partitionOption', 'partitionSettings']
                                },
                                dynamicContentTextClass: 'input-spaced'
                            },
                            {
                                type: 'parameterizable',
                                child: {
                                    type: 'textbox',
                                    bind: 'partitionUpperBound',
                                    placeholder: 'yyyy, e.g. 2019',
                                    label: stringResources.ModelName_Partition_UpperBound
                                },
                                dynamicContentTextClass: 'input-spaced'
                            },
                            {
                                type: 'parameterizable',
                                child: {
                                    type: 'textbox',
                                    bind: 'partitionLowerBound',
                                    placeholder: 'yyyy, e.g. 2018',
                                    label: stringResources.ModelName_Partition_LowerBound,
                                    multiple: false
                                },
                                dynamicContentTextClass: 'input-spaced'
                            },
                            {
                                type: 'parameterizable',
                                child: {
                                    type: 'textbox-number',
                                    bind: 'maxPartitionsNumber',
                                    label: stringResources.ModelName_MaxPartitionsNumber
                                },
                                dynamicContentTextClass: 'input-spaced'
                            }
                        ]
                    },
                    {
                        caseValue: partitionOptionConst.partitionOnCalendarMonth,
                        children: [
                            {
                                type: 'parameterizable',
                                child: {
                                    type: 'columns-selection',
                                    bind: 'partitionColumnName',
                                    label: stringResources.ModelName_Partition_ColumnName,
                                    includeCopySourceProperties: true,
                                    multiple: false,
                                    excludeSourceProperties: ['partitionOption', 'partitionSettings']
                                },
                                dynamicContentTextClass: 'input-spaced'
                            },
                            {
                                type: 'parameterizable',
                                child: {
                                    type: 'textbox',
                                    bind: 'partitionUpperBound',
                                    placeholder: 'yyyyMM, e.g. 201912',
                                    label: stringResources.ModelName_Partition_UpperBound
                                },
                                dynamicContentTextClass: 'input-spaced'
                            },
                            {
                                type: 'parameterizable',
                                child: {
                                    type: 'textbox',
                                    bind: 'partitionLowerBound',
                                    placeholder: 'yyyyMM, e.g. 201812',
                                    label: stringResources.ModelName_Partition_LowerBound
                                },
                                dynamicContentTextClass: 'input-spaced'
                            },
                            {
                                type: 'parameterizable',
                                child: {
                                    type: 'textbox-number',
                                    bind: 'maxPartitionsNumber',
                                    label: stringResources.ModelName_MaxPartitionsNumber
                                },
                                dynamicContentTextClass: 'input-spaced'
                            }
                        ]
                    },
                    {
                        caseValue: partitionOptionConst.partitionOnCalendarDate,
                        children: [
                            {
                                type: 'parameterizable',
                                child: {
                                    type: 'columns-selection',
                                    bind: 'partitionColumnName',
                                    multiple: false,
                                    includeCopySourceProperties: true,
                                    label: stringResources.ModelName_Partition_ColumnName,
                                    excludeSourceProperties: ['partitionOption', 'partitionSettings']
                                },
                                dynamicContentTextClass: 'input-spaced'
                            },
                            {
                                type: 'parameterizable',
                                child: {
                                    type: 'textbox',
                                    bind: 'partitionUpperBound',
                                    placeholder: 'yyyyMMdd, e.g. 20191231',
                                    label: stringResources.ModelName_Partition_UpperBound
                                },
                                dynamicContentTextClass: 'input-spaced'
                            },
                            {
                                type: 'parameterizable',
                                child: {
                                    type: 'textbox',
                                    bind: 'partitionLowerBound',
                                    placeholder: 'yyyyMMdd, e.g. 20181231',
                                    label: stringResources.ModelName_Partition_LowerBound
                                },
                                dynamicContentTextClass: 'input-spaced'
                            },
                            {
                                type: 'parameterizable',
                                child: {
                                    type: 'textbox-number',
                                    bind: 'maxPartitionsNumber',
                                    label: stringResources.ModelName_MaxPartitionsNumber
                                },
                                dynamicContentTextClass: 'input-spaced'
                            }
                        ]
                    },
                    {
                        caseValue: partitionOptionConst.partitionOnTime,
                        children: [
                            {
                                type: 'parameterizable',
                                child: {
                                    type: 'columns-selection',
                                    bind: 'partitionColumnName',
                                    multiple: false,
                                    includeCopySourceProperties: true,
                                    label: stringResources.ModelName_Partition_ColumnName,
                                    excludeSourceProperties: ['partitionOption', 'partitionSettings']
                                },
                                dynamicContentTextClass: 'input-spaced'
                            },
                            {
                                type: 'parameterizable',
                                child: {
                                    type: 'textbox',
                                    bind: 'partitionUpperBound',
                                    placeholder: 'Hhmmss, e.g. 235959',
                                    label: stringResources.ModelName_Partition_UpperBound
                                },
                                dynamicContentTextClass: 'input-spaced'
                            },
                            {
                                type: 'parameterizable',
                                child: {
                                    type: 'textbox',
                                    bind: 'partitionLowerBound',
                                    placeholder: 'Hhmmss, e.g. 000000',
                                    label: stringResources.ModelName_Partition_LowerBound
                                },
                                dynamicContentTextClass: 'input-spaced'
                            },
                            {
                                type: 'parameterizable',
                                child: {
                                    type: 'textbox-number',
                                    bind: 'maxPartitionsNumber',
                                    label: stringResources.ModelName_MaxPartitionsNumber
                                },
                                dynamicContentTextClass: 'input-spaced'
                            }
                        ]
                    }
                ]
            },
            {
                type: 'parameterizable',
                hide: () => {
                    return !exposureControlService.showSapTableRowSkip;
                },
                child: {
                    type: 'textbox-number',
                    bind: 'batchSize',
                    label: stringResources.ModelName_BatchSize,
                    description: stringResources.SapTable_BatchSize_Description
                },
                dynamicContentTextClass: 'input-spaced'
            },
            sapColumnDelimiterMetadata
        ],
        copyJob: {
            manualTableInput: true,
            multipleQuery: GlobalVariables.copyJobSAPTableQueryMode,
            sourcePartition: {
                supportSourcePartition: true
            }
        }
    };

    return metadata;
};
